<script>
    import { selectedItem } from '../../store/store.js';

    let showEstimated = false;
    let showExpected = false;

    // Major renovation keywords
    const majorKeywords = ['stambyte', 'stammar', 'fasad', 'tak', 'fönster', 'dränering', 'relining', 'hissar', 'hiss'];

    const renovationCycles = [
        { details: ['Stamrenovering', 'Stambyte', 'stammar', 'stam'], timeframe: [30, 60], displayName: 'Stambyte' },
        { details: ['Fasadrenovering', 'fasad', 'fasadmåleri'], timeframe: [30, 60], displayName: 'Fasadrenovering' },
        { details: ['Takrenovering', 'tak', 'takyta'], timeframe: [30, 50], displayName: 'Takrenovering' },
        { details: ['Fönster', 'fönster', 'fönsterbyte'], timeframe: [30, 50], displayName: 'Fönster' },
        { details: ['Elstambyte', 'elstambyte', 'elektrisk stam'], timeframe: [40, 60], displayName: 'Elstambyte' },
        { details: ['Grundisolering', 'Dränering', 'dränering', 'grundisoler', 'grund'], timeframe: [20, 30], displayName: 'Grundisolering (Dränering)' },
        { details: ['Relining', 'relining', 'spillvattenrör', 'avloppsrör'], timeframe: [20, 30], displayName: 'Relining av spillvattenrör' },
        { details: ['cirkulationspump', 'cirkpump', 'värmepump'], timeframe: [10, 20], displayName: 'Byte av cirkulationspump' },
        { details: ['OVK', 'ventilationskontroll', 'ventilation'], timeframe: [3, 6], displayName: 'Obligatorisk ventilationskontroll (OVK)' },
        { details: ['Stamspolning', 'stamspolning', 'spolning'], timeframe: [3, 5], displayName: 'Stamspolning' },
    ];

    let groupedPlan = {
        completed: [],
        planned: [],
        estimated: []
    };

    let expectedRenovations = [];

    $: {
        if (!$selectedItem) {
            groupedPlan = {
                completed: [],
                planned: [],
                estimated: []
            };
            expectedRenovations = [];
        } else {

        const plan = [];
        const completedRenovationsWithYear = [];

        // Helper to add plans and avoid duplicates
        const existingDetails = new Set();
        const addPlan = (item) => {
            if (item.details && !existingDetails.has(item.details)) {
                plan.push(item);
                existingDetails.add(item.details);
            }
        };

        // Completed renovations
        if ($selectedItem.completed_renovation) {
            if (Array.isArray($selectedItem.completed_renovation)) {
                $selectedItem.completed_renovation.forEach(reno => {
                    if (reno.renovation_project_name) {
                        const isMajor = majorKeywords.some(keyword => reno.renovation_project_name.toLowerCase().includes(keyword));
                        addPlan({
                            item: 'Genomfört Underhåll',
                            details: reno.renovation_project_name,
                            year: reno.renovation_year ? parseInt(reno.renovation_year) : null,
                            status: 'Genomfört',
                            isMajor: isMajor
                        });
                        
                        // Store for expected renovations calculation
                        if (reno.renovation_year && reno.renovation_project_name) {
                            completedRenovationsWithYear.push({
                                name: reno.renovation_project_name,
                                year: parseInt(reno.renovation_year)
                            });
                        }
                    }
                });
            } else if (typeof $selectedItem.completed_renovation === 'string') {
                const details = $selectedItem.completed_renovation;
                const isMajor = majorKeywords.some(keyword => details.toLowerCase().includes(keyword));
                addPlan({
                    item: 'Genomfört Underhåll',
                    details: details,
                    year: null,
                    status: 'Genomfört',
                    isMajor: isMajor
                });
            }
        }

        // Upcoming renovations
        if ($selectedItem.upcoming_renovation) {
            if (Array.isArray($selectedItem.upcoming_renovation)) {
                $selectedItem.upcoming_renovation.forEach(reno => {
                    if (reno.renovation_project_name) {
                        const isMajor = majorKeywords.some(keyword => reno.renovation_project_name.toLowerCase().includes(keyword));
                        addPlan({
                            item: 'Planerat Underhåll',
                            details: reno.renovation_project_name,
                            year: reno.renovation_year ? parseInt(reno.renovation_year) : null,
                            status: 'Planerat',
                            isMajor: isMajor
                        });
                    }
                });
            } else if (typeof $selectedItem.upcoming_renovation === 'string') {
                const details = $selectedItem.upcoming_renovation;
                const isMajor = majorKeywords.some(keyword => details.toLowerCase().includes(keyword));
                addPlan({
                    item: 'Planerat Underhåll',
                    details: details,
                    year: null,
                    status: 'Planerat',
                    isMajor: isMajor
                });
            }
        }

        // Maintenance plan from stadgar (bylaws)
        if ($selectedItem.maintenance_plan && $selectedItem.maintenance_plan.maintenance_schedule) {
            const details = $selectedItem.maintenance_plan.maintenance_schedule;
            if (details && !details.toLowerCase().includes("enligt underhållsplan") && !details.toLowerCase().includes("okänd")) {
                 const isMajor = majorKeywords.some(keyword => details.toLowerCase().includes(keyword));
                 addPlan({
                    item: 'Underhållsplan (stadgar)',
                    details: details,
                    year: null,
                    status: 'Planerat',
                    isMajor: isMajor
                });
            }
        }

        const completed = plan.filter(item => item.status === 'Genomfört').sort((a, b) => (b.year || 0) - (a.year || 0));
        const planned = plan.filter(item => item.status === 'Planerat').sort((a, b) => (b.year || 0) - (a.year || 0));
        const estimated = plan.filter(item => item.status === 'Diskuteras' || item.status?.toLowerCase() === 'diskuteras');

        // Calculate expected renovations based on completed renovations
        const expectedByCycle = new Map();
        const currentYear = new Date().getFullYear();
        
        completedRenovationsWithYear.forEach(completedReno => {
            const renoNameLower = completedReno.name.toLowerCase();
            
            renovationCycles.forEach(cycle => {
                const keywords = Array.isArray(cycle.details) ? cycle.details : [cycle.details];
                const matches = keywords.some(keyword => renoNameLower.includes(keyword.toLowerCase()));
                
                if (matches) {
                    let nextStartYear = completedReno.year + cycle.timeframe[0];
                    const nextEndYear = completedReno.year + cycle.timeframe[1];
                    
                    // Skip if the end year has already passed
                    if (nextEndYear < currentYear) {
                        return;
                    }
                    
                    // Set minimum start year to current year if it's in the past
                    if (nextStartYear < currentYear) {
                        nextStartYear = currentYear;
                    }
                    
                    const timeframeStr = `${nextStartYear}-${nextEndYear}`;
                    const displayName = cycle.displayName || (Array.isArray(cycle.details) ? cycle.details[0] : cycle.details);
                    const isUrgent = nextStartYear <= currentYear + 5;
                    
                    if (!expectedByCycle.has(displayName)) {
                        expectedByCycle.set(displayName, {
                            details: displayName,
                            timeframe: timeframeStr,
                            nextStartYear: nextStartYear,
                            completedYear: completedReno.year,
                            isUrgent: isUrgent
                        });
                    } else {
                        const existing = expectedByCycle.get(displayName);
                        // Keep the one with the earliest next start year (most urgent)
                        if (nextStartYear < existing.nextStartYear) {
                            expectedByCycle.set(displayName, {
                                details: displayName,
                                timeframe: timeframeStr,
                                nextStartYear: nextStartYear,
                                completedYear: completedReno.year,
                                isUrgent: isUrgent
                            });
                        }
                    }
                }
            });
        });
        
        expectedRenovations = Array.from(expectedByCycle.values()).map(item => ({
            details: item.details,
            timeframe: item.timeframe,
            isUrgent: item.isUrgent
        }));

        groupedPlan = {
            completed,
            planned,
            estimated
        };
        }
    }
</script>

<div class="card list-card maintenance-card">
    <div class="header-card">
        <h5><i class="fa-solid fa-wrench fa-sm icon-detail"></i> Underhållsplan</h5>
    </div>
    <div class="info-section">
        <p>Att ha en bra, uppdaterad underhållsplan är nyckeln till att på bästa sätt ta hand om föreningens fastighet – både på lång och kort sikt. Den ger också en bas för en långsiktig budget och rätt nivå på månadsavgifterna.</p>
        <p>Genom en underhållsplan får föreningen koll på det underhåll som ligger framåt i tiden och kan upprätthålla och utveckla fastighetens värde. Genom att samordna liknande underhåll kan åtgärderna göras mer kostnadseffektivt. En uppdaterad underhållsplan skapar även kontinuitet när styrelser kommer och går, och ger information om vad som är gjort och vad som ligger i planen framöver.</p>
    </div>

    {#if groupedPlan.planned.length > 0}
        <div class="maintenance-section planned-section">
            <h6 class="section-title">Planerade renoveringar</h6>
            <ul>
                {#each groupedPlan.planned as item}
                    <li class:major-renovation={item.isMajor}>
                        {#if item.year}
                            <span class="item-year">{item.year}</span>
                        {/if}
                        <div class="item-content">
                            <span class="item-details">{item.details.charAt(0).toUpperCase() + item.details.slice(1)}</span>
                            {#if item.isMajor}
                                <span class="major-flag"><i class="fa-solid fa-triangle-exclamation fa-sm icon-detail"></i> Potentiellt stor renovering planerad</span>
                            {/if}
                        </div>
                        <span class="status planerat">Planerat</span>
                    </li>
                {/each}
            </ul>
        </div>
    {/if}

    {#if groupedPlan.completed.length > 0}
        <div class="maintenance-section completed-section">
            <h6 class="section-title">Genomförda renoveringar</h6>
            <ul>
                {#each groupedPlan.completed as item}
                    <li>
                        {#if item.year}
                            <span class="item-year">{item.year}</span>
                        {/if}
                        <div class="item-content">
                            <span class="item-details">{item.details.charAt(0).toUpperCase() + item.details.slice(1)}</span>
                        </div>
                        <span class="status genomfört">Genomfört</span>
                    </li>
                {/each}
            </ul>
        </div>
    {/if}

    {#if groupedPlan.estimated.length > 0}
        <div class="maintenance-section estimated-section">
            <button class="collapse-toggle" on:click={() => showEstimated = !showEstimated}>
                <span class="section-title">Estimerade renoveringar i framtiden</span>
                <span class="toggle-icon" class:expanded={showEstimated}>▼</span>
            </button>
            {#if showEstimated}
                <ul>
                    {#each groupedPlan.estimated as item}
                        <li>
                            <div class="item-content">
                                <span class="item-details">{item.details.charAt(0).toUpperCase() + item.details.slice(1)}</span>
                            </div>
                            <span class="status diskuteras">Diskuteras</span>
                        </li>
                    {/each}
                </ul>
            {/if}
        </div>
    {/if}

    {#if expectedRenovations.length > 0}
        <div class="maintenance-section expected-section">
            <button class="collapse-toggle" on:click={() => showExpected = !showExpected}>
                <span class="section-title">Förväntade renoveringar (ej bekräftade)</span>
                <span class="toggle-icon" class:expanded={showExpected}>▼</span>
            </button>
            {#if showExpected}
                <p>Dessa förväntade renoveringar baseras på tidigare genomförda renoveringar och typiska underhållscykler. </p>
                <p>Detta är endast vägledande information - kontakta föreningen för aktuella planer.</p>
                <br>
                <ul>
                    {#each expectedRenovations as item}
                        <li class:urgent={item.isUrgent}>
                            <div class="item-content">
                                <span class="item-details">{item.details}</span>
                                <span class="timeframe">Förväntad tidsperiod: {item.timeframe}</span>
                            </div>
                            <span class="status förväntad">Förväntad</span>
                        </li>
                    {/each}
                </ul>
            {/if}
        </div>
    {/if}

    {#if groupedPlan.completed.length === 0 && groupedPlan.planned.length === 0 && groupedPlan.estimated.length === 0}
        <p class="no-data">Ingen underhållsplan tillgänglig. Kontrollera årsredovisningen för information om planerade underhållsåtgärder.</p>
    {/if}
</div>

<style>
    .card {
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border-radius: 24px;
        padding: 1.5rem;
        border: none;
        box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15);
        max-width: 100%;
        width: 100%;
        box-sizing: border-box;
    }

    .maintenance-card {
        margin: 0 auto;
        margin-bottom: 2rem;
        margin-top: 2rem;
    }

    .info-section {
        margin-bottom: 2rem;
        padding-bottom: 1.5rem;
        border-bottom: 2px solid #e9ecef;
        text-align: left;
    }

    .info-section p {
        margin: 0 0 1rem 0;
        line-height: 1.7;
        color: #444;
        font-size: 0.95rem;

    }

    .info-section p:last-child {
        margin-bottom: 0;
    }

    .maintenance-section {
        margin-bottom: 2rem;
    }

    .maintenance-section:last-child {
        margin-bottom: 0;
    }

    .section-title {
        font-size: 1rem;
        font-weight: 600;
        color: #333;
        margin: 0 0 1rem 0;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .planned-section .section-title {
        color: #856404;
    }

    .completed-section .section-title {
        color: #155724;
    }

    .estimated-section .section-title {
        color: #495057;
    }

    .expected-section .section-title {
        color: #6c757d;
    }

    .maintenance-section ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }

    .maintenance-section li {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: 1rem;
        background-color: #f8f9fa;
        border-radius: 8px;
        border-left: 3px solid #dee2e6;
        transition: background-color 0.2s;
    }

    .maintenance-section li:hover {
        background-color: #e9ecef;
    }

    .maintenance-section li.major-renovation {
        border-left-color: #ffc107;
    }

    .maintenance-section li.urgent {
        background-color: #fff3cd;
        border-left-color: #ffc107;
    }

    .item-year {
        font-size: 0.85rem;
        font-weight: 500;
        color: #6c757d;
        min-width: 50px;
        padding-right: 1rem;
        flex-shrink: 0;
    }

    .item-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .item-details {
        font-size: 0.95rem;
        color: #333;
        font-weight: 500;
    }

    .major-flag {
        font-size: 0.85rem;
        color: #856404;
        font-weight: 500;
    }

    .status {
        padding: 0.4rem 0.9rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
        white-space: nowrap;
        flex-shrink: 0;
        margin-left: 1rem;
    }

    .status.genomfört {
        background-color: #d4edda;
        color: #155724;
    }

    .status.planerat {
        background-color: #fff3cd;
        color: #856404;
    }

    .status.diskuteras {
        background-color: #e2e3e5;
        color: #383d41;
    }

    .status.förväntad {
        background-color: #d1ecf1;
        color: #0c5460;
    }

    .collapse-toggle {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.75rem 0;
        background: none;
        border: none;
        cursor: pointer;
        text-align: left;
    }

    .collapse-toggle:hover {
        opacity: 0.8;
    }

    .toggle-icon {
        font-size: 0.75rem;
        color: #6c757d;
        transition: transform 0.2s;
    }

    .toggle-icon.expanded {
        transform: rotate(180deg);
    }

    .timeframe {
        font-size: 0.85rem;
        color: #6c757d;
        font-weight: 500;
        margin-top: 0.25rem;
    }

    .no-data {
        text-align: center;
        color: #6c757d;
        padding: 2rem;
        font-style: italic;
    }

    @media (max-width: 768px) {
        .card {
            padding: 1rem;
            border-radius: 12px;
        }
        
        .list-card h5 {
            font-size: 1.1rem;
            margin-bottom: 1rem;
        }

        .info-section {
            margin-bottom: 1.5rem;
            padding-bottom: 1rem;
        }

        .info-section p {
            font-size: 0.9rem;
            line-height: 1.6;
        }

        .maintenance-section {
            margin-bottom: 1.5rem;
        }

        .maintenance-section li {
            flex-direction: column;
            padding: 0.75rem;
            gap: 0.75rem;
        }

        .item-year {
            min-width: auto;
            padding-right: 0;
            margin-bottom: 0.5rem;
        }

        .status {
            margin-left: 0;
            align-self: flex-start;
        }

        .section-title {
            font-size: 0.9rem;
        }
    }

    @media (min-width: 769px) and (max-width: 1024px) {
        .maintenance-card {
            max-width: 100%;
        }
    }
</style>
